<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { getLibraryExternalLinks, type LibraryExternalLink } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import { resolveExternalLink, type ExternalLinkCode } from './externalLinks.ts';

    const props = defineProps<{
        libraryName: string;
        code: ExternalLinkCode;
    }>();

    const { showError } = useShow();

    const links = ref<LibraryExternalLink[]>([]);

    // The resolver withholds the url either because a placeholder has no value for this code, or
    // because the configured template does not resolve to an http(s) address. The first is normal
    // for an incomplete code, the second is a library configuration error.
    // A link that is both is reported as missing-value; either way it never becomes an anchor.
    const resolvedLinks = computed(() => links.value.map(link => {
        const resolved = resolveExternalLink(link, props.code);
        return { ...resolved, unsafe: !resolved.url && resolved.missing.length === 0 };
    }));

    const listedLinks = computed(() => resolvedLinks.value.filter(link => !link.unsafe));
    const unsafeLinks = computed(() => resolvedLinks.value.filter(link => link.unsafe));

    function missingValuesText(missing: string[]): string {
        if (missing.length === 1)
            return `${missing[0]} value needed to enable link.`;

        return `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]} values needed to enable link.`;
    }

    onMounted(async () => {
        try {
            links.value = await getLibraryExternalLinks(http, props.libraryName);
        } catch (error) {
            showError(error);
        }
    });
</script>

<template>
    <div
        v-if="resolvedLinks.length > 0"
        class="box">
        <h2 class="title is-5">
            External links
        </h2>
        <b-notification
            v-for="(link, index) in unsafeLinks"
            :key="`unsafe-${index}`"
            type="is-warning"
            :closable="false"
            has-icon
            role="alert">
            The external link <b>{{ link.text }}</b> is not shown: the configured URL was rejected because it is not a
            valid http(s) address. An administrator needs to fix the library configuration.
        </b-notification>
        <ul
            v-if="listedLinks.length > 0"
            class="bullet-list">
            <li
                v-for="(link, index) in listedLinks"
                :key="index">
                <a
                    v-if="link.url"
                    :href="link.url"
                    target="_blank"
                    rel="noopener noreferrer">{{ link.text }}</a>
                <template v-else>
                    <span>{{ link.text }}</span>
                    <span
                        class="ml-2 has-text-warning-dark"
                        data-testid="missing-values">
                        <b-icon
                            pack="mdi"
                            icon="alert-outline"
                            size="is-small"
                            type="is-warning" />
                        {{ missingValuesText(link.missing) }}
                    </span>
                </template>
            </li>
        </ul>
    </div>
</template>
